import { useState, useEffect } from 'react';
import { Download, Share, Plus, Smartphone, X } from 'lucide-react';

const InstallAppBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [deviceType, setDeviceType] = useState(null);

  // Rileva iOS immediatamente (prima di qualsiasi useEffect)
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent || '');
  
  useEffect(() => {
    // Su iOS non fare NULLA - esci subito
    if (isIOS) {
      console.log('iOS detected - InstallAppBanner disabled');
      return;
    }
    
    // Controlla se già installata (standalone mode)
    let isStandalone = false;
    try {
      isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    } catch (e) {
      console.log('Standalone check error:', e);
    }
    
    if (isStandalone) return;

    // Rileva tipo dispositivo (solo Android a questo punto)
    const userAgent = navigator.userAgent || '';
    const isAndroid = /android/i.test(userAgent);

    // Controlla localStorage - dismissal valido per 7 giorni
    try {
      const dismissedTime = localStorage.getItem('pwa_install_dismissed_time');
      if (dismissedTime) {
        const daysSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
        if (daysSinceDismissed < 7) return;
      }
    } catch (e) {
      console.log('LocalStorage check error:', e);
    }

    // Mostra solo su Android
    if (isAndroid) {
      setDeviceType('android');
      setTimeout(() => setShowBanner(true), 500);
    }

    // Android: intercetta evento beforeinstallprompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, [isIOS]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        localStorage.setItem('pwa_install_dismissed_time', Date.now().toString());
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleContinueWeb = () => {
    localStorage.setItem('pwa_install_dismissed_time', Date.now().toString());
    setShowBanner(false);
  };

  // Su iOS non renderizzare MAI nulla
  if (isIOS) return null;
  
  if (!showBanner) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(180deg, #0a0f1a 0%, #0f172a 50%, #0a0f1a 100%)',
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        overflow: 'auto'
      }}
    >
      {/* Chiudi in alto a destra */}
      <button
        onClick={handleContinueWeb}
        style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          backgroundColor: 'rgba(51, 65, 85, 0.5)',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer'
        }}
      >
        <X style={{ width: '20px', height: '20px', color: '#94a3b8' }} />
      </button>

      {/* Logo */}
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <div style={{
          width: '120px',
          height: '120px',
          margin: '0 auto 24px',
          borderRadius: '28px',
          overflow: 'hidden',
          border: '3px solid rgba(6, 182, 212, 0.6)',
          boxShadow: '0 0 40px rgba(6, 182, 212, 0.4), 0 0 80px rgba(6, 182, 212, 0.2)'
        }}>
          <img 
            src="https://customer-assets.emergentagent.com/job_respiro-dashboard/artifacts/barokarc_IMG_6596.jpeg" 
            alt="Respiro Blu" 
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
        <h1 style={{ 
          fontSize: '32px', 
          fontWeight: 'bold', 
          color: 'white', 
          marginBottom: '8px',
          fontFamily: 'Barlow Condensed, sans-serif',
          textShadow: '0 0 20px rgba(6, 182, 212, 0.5)'
        }}>
          RESPIRO BLU
        </h1>
        <p style={{ color: '#22d3ee', fontSize: '18px', marginBottom: '4px', fontWeight: '500' }}>
          Citizen Science per il Mediterraneo
        </p>
        <p style={{ color: '#94a3b8', fontSize: '14px' }}>Diventa un Custode del Mare</p>
      </div>

      {/* Card principale */}
      <div style={{
        backgroundColor: 'rgba(15, 23, 42, 0.9)',
        border: '1px solid rgba(6, 182, 212, 0.4)',
        borderRadius: '20px',
        padding: '28px',
        width: '100%',
        maxWidth: '380px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          gap: '8px', 
          marginBottom: '24px',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          padding: '8px 16px',
          borderRadius: '20px'
        }}>
          <Smartphone style={{ width: '18px', height: '18px', color: '#34d399' }} />
          <span style={{ color: '#34d399', fontSize: '14px', fontWeight: '500' }}>
            {deviceType === 'ios' ? 'iPhone / iPad' : 'Android'}
          </span>
        </div>

        <p style={{ 
          color: 'white', 
          textAlign: 'center', 
          fontSize: '20px', 
          fontWeight: '600',
          marginBottom: '8px' 
        }}>
          Installa l'App Gratuita
        </p>
        <p style={{ 
          color: '#94a3b8', 
          textAlign: 'center', 
          fontSize: '14px', 
          marginBottom: '24px' 
        }}>
          Accesso rapido, notifiche e funziona offline!
        </p>

        {deviceType === 'ios' ? (
          <div style={{ marginBottom: '24px' }}>
            {/* iOS Steps */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '16px', 
              backgroundColor: 'rgba(30, 41, 59, 0.6)', 
              borderRadius: '14px', 
              padding: '16px', 
              marginBottom: '12px',
              border: '1px solid rgba(59, 130, 246, 0.3)'
            }}>
              <div style={{ 
                width: '48px', 
                height: '48px', 
                borderRadius: '12px', 
                backgroundColor: 'rgba(59, 130, 246, 0.2)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Share style={{ width: '24px', height: '24px', color: '#60a5fa' }} />
              </div>
              <div>
                <p style={{ color: 'white', fontSize: '16px', fontWeight: '600', marginBottom: '2px' }}>
                  1. Tocca Condividi
                </p>
                <p style={{ color: '#94a3b8', fontSize: '13px' }}>
                  L'icona <span style={{ color: '#60a5fa' }}>↑</span> in basso su Safari
                </p>
              </div>
            </div>
            
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '16px', 
              backgroundColor: 'rgba(30, 41, 59, 0.6)', 
              borderRadius: '14px', 
              padding: '16px', 
              marginBottom: '12px',
              border: '1px solid rgba(6, 182, 212, 0.3)'
            }}>
              <div style={{ 
                width: '48px', 
                height: '48px', 
                borderRadius: '12px', 
                backgroundColor: 'rgba(6, 182, 212, 0.2)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Plus style={{ width: '24px', height: '24px', color: '#22d3ee' }} />
              </div>
              <div>
                <p style={{ color: 'white', fontSize: '16px', fontWeight: '600', marginBottom: '2px' }}>
                  2. "Aggiungi a Home"
                </p>
                <p style={{ color: '#94a3b8', fontSize: '13px' }}>Scorri e trova l'opzione nel menu</p>
              </div>
            </div>
            
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '16px', 
              backgroundColor: 'rgba(30, 41, 59, 0.6)', 
              borderRadius: '14px', 
              padding: '16px',
              border: '1px solid rgba(16, 185, 129, 0.3)'
            }}>
              <div style={{ 
                width: '48px', 
                height: '48px', 
                borderRadius: '12px', 
                backgroundColor: 'rgba(16, 185, 129, 0.2)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Smartphone style={{ width: '24px', height: '24px', color: '#34d399' }} />
              </div>
              <div>
                <p style={{ color: 'white', fontSize: '16px', fontWeight: '600', marginBottom: '2px' }}>
                  3. Tocca "Aggiungi"
                </p>
                <p style={{ color: '#94a3b8', fontSize: '13px' }}>L'app sarà sulla tua Home!</p>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: '24px' }}>
            {deferredPrompt ? (
              <button
                onClick={handleInstallClick}
                style={{
                  width: '100%',
                  padding: '18px',
                  background: 'linear-gradient(135deg, #06b6d4 0%, #2563eb 100%)',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '18px',
                  border: 'none',
                  borderRadius: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  boxShadow: '0 4px 20px rgba(6, 182, 212, 0.4)'
                }}
              >
                <Download style={{ width: '24px', height: '24px' }} />
                Installa Ora
              </button>
            ) : (
              <>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '16px', 
                  backgroundColor: 'rgba(30, 41, 59, 0.6)', 
                  borderRadius: '14px', 
                  padding: '16px', 
                  marginBottom: '12px',
                  border: '1px solid rgba(59, 130, 246, 0.3)'
                }}>
                  <div style={{ 
                    width: '48px', 
                    height: '48px', 
                    borderRadius: '12px', 
                    backgroundColor: 'rgba(59, 130, 246, 0.2)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <span style={{ fontSize: '24px', color: '#60a5fa' }}>⋮</span>
                  </div>
                  <div>
                    <p style={{ color: 'white', fontSize: '16px', fontWeight: '600', marginBottom: '2px' }}>
                      1. Tocca il menu ⋮
                    </p>
                    <p style={{ color: '#94a3b8', fontSize: '13px' }}>In alto a destra nel browser</p>
                  </div>
                </div>
                
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '16px', 
                  backgroundColor: 'rgba(30, 41, 59, 0.6)', 
                  borderRadius: '14px', 
                  padding: '16px',
                  border: '1px solid rgba(6, 182, 212, 0.3)'
                }}>
                  <div style={{ 
                    width: '48px', 
                    height: '48px', 
                    borderRadius: '12px', 
                    backgroundColor: 'rgba(6, 182, 212, 0.2)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Download style={{ width: '24px', height: '24px', color: '#22d3ee' }} />
                  </div>
                  <div>
                    <p style={{ color: 'white', fontSize: '16px', fontWeight: '600', marginBottom: '2px' }}>
                      2. "Installa app"
                    </p>
                    <p style={{ color: '#94a3b8', fontSize: '13px' }}>O "Aggiungi a schermata Home"</p>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Continua */}
        <button
          onClick={handleContinueWeb}
          style={{
            width: '100%',
            padding: '14px',
            backgroundColor: 'transparent',
            color: '#94a3b8',
            border: '1px solid rgba(100, 116, 139, 0.3)',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '15px'
          }}
        >
          Continua sul browser
        </button>
      </div>

      {/* Footer */}
      <div style={{ marginTop: '32px', textAlign: 'center' }}>
        <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '4px' }}>
          ✓ Gratuita  ✓ Nessun download da store  ✓ Funziona offline
        </p>
      </div>
    </div>
  );
};

export default InstallAppBanner;
